import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, Star } from "@mui/icons-material";
import { getServingMicronutrients, formatNutrientUnit, scaleServingFrom, formatAmount } from "./foodDictionaryNutrientUtils";
import { foodDictionaryErrorMessage } from "./foodDictionaryErrorMessage";
import { t, macro as M } from "../dashboard/coach/coachTokens";
import BrandButton from "./BrandButton";
import { RTL } from "../../cache/CacheRtl";
import {
  addFoodDictionaryServing,
  getNutrientDefinitions,
  removeFoodDictionaryServing,
  updateFoodDictionaryServing,
} from "../../services/system/FoodDictionaryService";
import {
  addServing as addCompanyServing,
  getNutrientDefinitions as getCompanyNutrientDefinitions,
  removeServing as removeCompanyServing,
  updateServing as updateCompanyServing,
} from "../../services/FoodDictionaryService";
import { useMessageContext } from "../../contexts/MessageContext";
import { useAuth } from "../../contexts/AuthContext";

const emptyForm = () => ({
  servingName: "",
  gramsPerServing: "",
  calories: "",
  protein: "",
  carbs: "",
  fats: "",
  isDefault: false,
  micronutrients: {},
});

const micronutrientsFromServing = (food, serving) => {
  const micros = getServingMicronutrients(food, serving);
  return micros.reduce((acc, n) => {
    acc[n.code] = n.amount?.toString() || "";
    return acc;
  }, {});
};


// Compact macro pill: "ח׳ 20.1"
const MacroPill = ({ label, value, color }) => (
  <Box sx={{ display: "inline-flex", alignItems: "baseline", gap: "3px", whiteSpace: "nowrap" }}>
    <Typography component="span" sx={{ fontSize: "11px", color: "#94A3B8", fontWeight: 600 }}>{label}</Typography>
    <Typography component="span" sx={{ fontSize: "13px", fontWeight: 700, color }}>{value}</Typography>
  </Box>
);

/**
 * One portion, and the control that points the nutrition panel at it.
 *
 * <p>Selection and default are two different things and are shown as two different things: the star
 * marks the portion the food is normally logged in, the ring marks the one you are currently reading.
 * They start out the same and stop being the same the moment you click another row, which is exactly
 * when conflating them would mislead.
 */
const ServingCard = ({ food, serving, canEdit, selected, onSelect, onEdit, onDelete }) => {
  const nv = serving.nutritionValues || {};
  const fmt = formatAmount;

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`הצג ערכים תזונתיים עבור ${serving.name}`}
      onClick={() => onSelect?.(serving)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect?.(serving);
        }
      }}
      sx={{
        border: `1px solid ${selected ? "#0EA5E9" : serving.isDefault ? "#BAE6FD" : "#E2E8F0"}`,
        boxShadow: selected ? "0 0 0 2px rgba(14,165,233,.18)" : "none",
        borderRadius: "10px",
        background: selected || serving.isDefault ? "#F0F9FF" : "#FFFFFF",
        p: 1.25,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        cursor: "pointer",
        transition: "border-color .12s, box-shadow .12s, background .12s",
        "&:hover": { borderColor: "#0EA5E9" },
        "&:focus-visible": { outline: "2px solid #0EA5E9", outlineOffset: "2px" },
      }}
    >
      {/* Name + grams + chips */}
      <Box sx={{ minWidth: 0, flex: "1 1 38%", display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: "#1E293B", overflow: "hidden", textOverflow: "ellipsis" }}>
          {serving.name}
        </Typography>
        {serving.gramsPerServing != null && (
          <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>{formatAmount(serving.gramsPerServing)} גר'</Typography>
        )}
        {serving.isDefault && <Star sx={{ fontSize: 15, color: "#0EA5E9" }} />}
      </Box>

      {/* Macros inline */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, flexWrap: "wrap", flexShrink: 0 }}>
        <MacroPill label="קל׳" value={nv.calories ?? "—"} color="#1E293B" />
        <MacroPill label="ח׳" value={fmt(nv.protein)} color={M.protein.text} />
        <MacroPill label="פ׳" value={fmt(nv.carbs)} color={M.carbs.text} />
        <MacroPill label="ש׳" value={fmt(nv.fats)} color={M.fats.text} />
      </Box>

      {/* Actions. stopPropagation so opening the editor does not also re-point the nutrition panel —
          the click was aimed at the button, not at the row. */}
      {canEdit && (
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(serving); }} sx={{ color: "#64748B", p: 0.5 }}>
            <Edit sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(serving); }} sx={{ color: "#DC2626", p: 0.5 }}>
            <Delete sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

const FoodDictionaryServingsSection = ({
  food,
  canEdit = false,
  onUpdate,
  selectedServingId = null,
  onSelectServing,
}) => {
  const { notificationHandler } = useMessageContext();
  const { isSuperAdmin } = useAuth();

  // Which door this section knocks on. A system admin uses /system, which can reach any food in the
  // catalog; everyone else uses the company route, which refuses a food their company does not own.
  // A system food belongs to no company, so for a company admin these calls cannot touch one — the
  // hidden buttons above are the courtesy, this is the boundary.
  const api = isSuperAdmin()
    ? {
        add: addFoodDictionaryServing,
        update: updateFoodDictionaryServing,
        remove: removeFoodDictionaryServing,
        nutrients: getNutrientDefinitions,
      }
    : {
        add: addCompanyServing,
        update: updateCompanyServing,
        remove: removeCompanyServing,
        nutrients: getCompanyNutrientDefinitions,
      };
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingServing, setEditingServing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm());
  const [nutrientDefinitions, setNutrientDefinitions] = useState([]);
  // The portion a new one is being derived from, if any. Null means "I'll type the numbers myself".
  const [basisServingId, setBasisServingId] = useState("");

  useEffect(() => {
    if (!canEdit) return;
    api.nutrients()
      .then((r) => setNutrientDefinitions(r.data || []))
      .catch(() => notificationHandler.error("שגיאה בטעינת קטלוג ויטמינים ומינרלים"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  const handleOpenAdd = () => {
    setEditMode(false);
    setEditingServing(null);
    setFormData(emptyForm());
    setBasisServingId("");
    setOpenDialog(true);
  };

  /** Portions worth deriving from: they need a weight for the ratio to mean anything. */
  const basisCandidates = (food.servings || []).filter((s) => s.gramsPerServing > 0);

  /**
   * Recompute the numbers from the chosen portion whenever the basis or the target weight moves.
   *
   * <p>Only the derived fields are written, and only while a basis is selected — clearing the select
   * leaves whatever is on screen alone rather than wiping work. The name is never touched: "1 piece"
   * is the thing being described, not something that can be scaled.
   */
  const applyBasis = (servingId, grams) => {
    const source = basisCandidates.find((s) => String(s.id) === String(servingId));
    const derived = source && scaleServingFrom(food, source, grams);
    if (!derived) return;
    setFormData((p) => ({ ...p, ...derived }));
  };

  const handleBasisChange = (servingId) => {
    setBasisServingId(servingId);
    applyBasis(servingId, formData.gramsPerServing);
  };

  const handleGramsChange = (grams) => {
    setFormData((p) => ({ ...p, gramsPerServing: grams }));
    if (basisServingId) applyBasis(basisServingId, grams);
  };

  // The weight this portion had when the dialog opened, and the one now in the field.
  const originalGrams = Number(editingServing?.gramsPerServing) || 0;
  const targetGrams = Number(formData.gramsPerServing) || 0;
  const canRescaleToWeight =
    editMode && originalGrams > 0 && targetGrams > 0 && targetGrams !== originalGrams;

  /**
   * Re-derive every value from the weight the portion started at.
   *
   * <p>Correcting "15 גרם" to 20 g is a weight change, not a nutrition change — but the panel below
   * still describes 15 g, and retyping four macros and twenty micronutrients by hand is how a decimal
   * goes missing. Offered rather than applied: the same edit is sometimes a correction to the label
   * alone ("15 גרם" was always really 20 g), in which case rescaling would be exactly wrong.
   *
   * <p>Always scales from {@link editingServing}, never from what is currently in the form, so typing
   * 20 and then 25 lands on 25/15 rather than compounding 20/15 and then 25/20.
   */
  const handleRescaleToWeight = () => {
    const derived = scaleServingFrom(food, editingServing, formData.gramsPerServing);
    if (!derived) return;
    setFormData((p) => ({ ...p, ...derived }));
  };

  const handleOpenEdit = (serving) => {
    const nv = serving.nutritionValues || {};
    setEditMode(true);
    setEditingServing(serving);
    setFormData({
      servingName: serving.name || "",
      gramsPerServing: serving.gramsPerServing?.toString() || "",
      calories: nv.calories?.toString() || "",
      protein: nv.protein?.toString() || "",
      carbs: nv.carbs?.toString() || "",
      fats: nv.fats?.toString() || "",
      isDefault: !!serving.isDefault,
      micronutrients: micronutrientsFromServing(food, serving),
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setEditingServing(null);
    setFormData(emptyForm());
  };

  const buildPayload = () => {
    const micronutrients = Object.entries(formData.micronutrients || {})
      .filter(([, amount]) => amount !== "" && amount != null)
      .map(([code, amount]) => ({ code, amount: parseFloat(amount) }));

    return {
      servingName: formData.servingName.trim(),
      gramsPerServing: formData.gramsPerServing ? parseFloat(formData.gramsPerServing) : null,
      isDefault: formData.isDefault,
      nutritionValues: {
        calories: parseFloat(formData.calories),
        protein: formData.protein ? parseFloat(formData.protein) : null,
        carbs: formData.carbs ? parseFloat(formData.carbs) : null,
        fats: formData.fats ? parseFloat(formData.fats) : null,
      },
      micronutrients,
    };
  };

  const handleMicroChange = (code, value) => {
    setFormData((p) => ({
      ...p,
      micronutrients: { ...p.micronutrients, [code]: value },
    }));
  };

  const handleSave = async () => {
    if (!formData.servingName.trim()) {
      notificationHandler.error("יש למלא את שם המנה");
      return;
    }
    if (!formData.calories || parseFloat(formData.calories) < 0) {
      notificationHandler.error("יש להזין קלוריות תקינות");
      return;
    }

    setLoading(true);
    try {
      const payload = buildPayload();
      if (editMode) {
        await api.update(food.id, editingServing.id, payload);
        notificationHandler.success("המנה עודכנה");
      } else {
        await api.add(food.id, payload);
        notificationHandler.success("מנה נוספה");
      }
      handleCloseDialog();
      onUpdate?.();
    } catch (error) {
      notificationHandler.error(foodDictionaryErrorMessage(error, "שגיאה בשמירת המנה"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (serving) => {
    if (!window.confirm(`האם למחוק את המנה "${serving.name}"?`)) return;

    setLoading(true);
    try {
      await api.remove(food.id, serving.id);
      notificationHandler.success("המנה נמחקה");
      onUpdate?.();
    } catch (error) {
      notificationHandler.error(foodDictionaryErrorMessage(error, "שגיאה במחיקת המנה"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Paper sx={{ p: 3, borderRadius: "12px", boxShadow: "0 1px 3px rgba(16,24,40,.1)", direction: "rtl" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography sx={{ fontSize: "20px", fontWeight: 600, color: "#1E293B" }}>
            גדלי מנות ({food.servings?.length || 0})
          </Typography>
          {canEdit && (
            <BrandButton variant="primary" icon={<Add sx={{ fontSize: 16 }} />} onClick={handleOpenAdd}>
              הוסף מנה
            </BrandButton>
          )}
        </Box>

        {food.servings?.length ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {food.servings.map((serving) => (
              <ServingCard
                key={serving.id}
                food={food}
                serving={serving}
                canEdit={canEdit}
                selected={serving.id === selectedServingId}
                onSelect={onSelectServing}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
              />
            ))}
          </Box>
        ) : (
          <Typography sx={{ color: "#94A3B8", textAlign: "center", py: 4 }}>אין מנות מוגדרות</Typography>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: t.r, direction: "rtl" } }}>
        <DialogTitle sx={{ fontSize: "18px", fontWeight: 700, color: t.text }}>
          {editMode ? "עריכת מנה" : "הוספת מנה חדשה"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
            <RTL>
              <TextField
                fullWidth
                label="שם המנה *"
                placeholder="למשל: כף, כוס, 100 גרם"
                value={formData.servingName}
                onChange={(e) => setFormData((p) => ({ ...p, servingName: e.target.value }))}
              />
            </RTL>
            <RTL>
              <TextField
                fullWidth
                label="משקל (גרם)"
                type="number"
                placeholder="למשל: 15"
                value={formData.gramsPerServing}
                onChange={(e) => handleGramsChange(e.target.value)}
                inputProps={{ min: 0, step: 0.1 }}
              />
            </RTL>

            {/* Editing the weight of an existing portion: offer the same arithmetic, on request. */}
            {canRescaleToWeight && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                  flexWrap: "wrap",
                  p: 1.5,
                  borderRadius: "10px",
                  border: "1px solid #BAE6FD",
                  background: "#F0F9FF",
                }}
              >
                <Typography sx={{ fontSize: "13px", color: "#0C4A6E" }}>
                  המשקל שונה מ-{formatAmount(originalGrams)} ל-{formatAmount(targetGrams)} גרם.
                  אפשר לחשב מחדש את כל הערכים לפי היחס.
                </Typography>
                <BrandButton variant="secondary" onClick={handleRescaleToWeight}>
                  חשב מחדש לפי המשקל
                </BrandButton>
              </Box>
            )}

            {/* Derive-from. Offered on a new portion only: on an existing one the weight-change
                prompt above covers it, and a basis picker there would silently overwrite values
                someone entered deliberately. Works in both directions — 100 g down to a 30 g piece,
                or a 30 g spoon up to 100 g — because it is just a weight ratio. */}
            {!editMode && basisCandidates.length > 0 && (
              <RTL>
                <TextField
                  select
                  fullWidth
                  label="חשב לפי מנה קיימת"
                  value={basisServingId}
                  onChange={(e) => handleBasisChange(e.target.value)}
                  helperText={
                    basisServingId
                      ? "הערכים והמיקרו-נוטריאנטים חושבו לפי היחס במשקל. אפשר לערוך אותם."
                      : "בחר מנה קיימת כדי למלא את הערכים אוטומטית לפי המשקל"
                  }
                >
                  <MenuItem value="">ללא — הזנה ידנית</MenuItem>
                  {basisCandidates.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.name} ({formatAmount(s.gramsPerServing)} גר')
                    </MenuItem>
                  ))}
                </TextField>
              </RTL>
            )}

            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B" }}>ערכים תזונתיים למנה</Typography>

            <RTL>
              {/* Energy is fractional: a gram of banana is 0.89 kcal, a gram of cucumber 0.15. A whole
                  step, or parseInt on the way out, rounds that away — which is what this field did. */}
              <TextField
                fullWidth
                label="קלוריות *"
                type="number"
                value={formData.calories}
                onChange={(e) => setFormData((p) => ({ ...p, calories: e.target.value }))}
                inputProps={{ min: 0, step: "any" }}
              />
            </RTL>
            <RTL>
              <TextField
                fullWidth
                label="חלבון (גרם)"
                type="number"
                value={formData.protein}
                onChange={(e) => setFormData((p) => ({ ...p, protein: e.target.value }))}
                inputProps={{ min: 0, step: 0.1 }}
              />
            </RTL>
            <RTL>
              <TextField
                fullWidth
                label="פחמימות (גרם)"
                type="number"
                value={formData.carbs}
                onChange={(e) => setFormData((p) => ({ ...p, carbs: e.target.value }))}
                inputProps={{ min: 0, step: 0.1 }}
              />
            </RTL>
            <RTL>
              <TextField
                fullWidth
                label="שומן (גרם)"
                type="number"
                value={formData.fats}
                onChange={(e) => setFormData((p) => ({ ...p, fats: e.target.value }))}
                inputProps={{ min: 0, step: 0.1 }}
              />
            </RTL>

            <RTL>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDefault}
                    onChange={(e) => setFormData((p) => ({ ...p, isDefault: e.target.checked }))}
                    color="primary"
                  />
                }
                label="מנה ברירת מחדל"
              />
            </RTL>

            {nutrientDefinitions.length > 0 && (
              <>
                <Divider />
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B" }}>
                  ויטמינים ומינרלים למנה זו
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  {nutrientDefinitions.map((n) => (
                    <RTL key={n.code}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`${n.nameHe || n.nameEn || n.code} (${formatNutrientUnit(n.unit)})`}
                        type="number"
                        value={formData.micronutrients[n.code] || ""}
                        onChange={(e) => handleMicroChange(n.code, e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </RTL>
                  ))}
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <BrandButton variant="secondary" onClick={handleCloseDialog} disabled={loading}>ביטול</BrandButton>
          <BrandButton variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? "שומר..." : editMode ? "עדכן" : "הוסף"}
          </BrandButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FoodDictionaryServingsSection;
